import { CommonMainPage } from '../../widgets/utility/CommonMainPage'
import { BpTabs } from '../../shared/components/utility/tabs/BpTabs'
import { BpTab } from '../../shared/components/utility/tabs/tab/BpTab'
import {
  useGetProjectInfoQuery,
  useGetProjectLocationsQuery,
  useGetProjectTermsQuery,
  usePostProjectUpdateLocationsMutation,
  usePostProjectUpdateTermsMutation,
} from '../../app/api'
import { unstable_useBlocker as useBlocker, useNavigate, useParams } from 'react-router-dom'
import React, { useEffect, useState } from 'react'
import { IconButton } from '../../shared/components/buttons/icon-button/IconButton'
import { ProjectMainSettings } from './widgets/project-main-settings/ProjectMainSettings'
import { useAppDispatch, useAppSelector } from '../../app/store/hooks/Redux'
import { setModal } from '../../app/store/reducers/AppSlice'
import { ProjectLocations } from './widgets/project-locations/ProjectLocations'
import { ProjectEditName } from './widgets/edit-name-modal/ProjectEditName'
import { ProjectTerms } from './widgets/project-terms/ProjectTerms'
import { useTranslation } from 'react-i18next'
import { IconArrowBack } from '../../shared/components/icons/IconArrowBack'
import { IconPencil } from '../../shared/components/icons/IconPencil'
import { TariffButton } from '../../shared/components/buttons/tariff-button/TariffButton'
import {
  selectProjectLocations,
  selectProjectLocationsChanged,
  selectProjectTerms,
  selectProjectTermsChanged,
  setProjectLocations,
  setProjectTerms,
} from '../projects/model/projectsSlice'
import { UnsavedChangesModal } from './widgets/unsaved-changes-modal/UnsavedChangesModal'
import { ITerm } from '../projects/model/types/Term.interface'
import { MissingKeywordsModal } from './widgets/missing-keywords-modal/MissingKeywordsModal'
import { selectUser, setTariffUpdated } from '../../app/store/reducers/AuthSlice'
import { ProjectAffiliateRule } from './widgets/affiliate-rule/ProjectAffiliateRule'
import clsx from 'clsx'

import styles from './ProjectPage.module.scss'
import { ProjectTrademarks } from './widgets/project-trademarks/ProjectTrademarks'

export const ProjectPage = () => {
  const { t } = useTranslation()
  const { id, tab } = useParams()
  const navigate = useNavigate()

  const user = useAppSelector(selectUser)
  const locationsChanged = useAppSelector(selectProjectLocationsChanged)
  const locationsList = useAppSelector(selectProjectLocations)
  const termsChanged = useAppSelector(selectProjectTermsChanged)
  const termsList = useAppSelector(selectProjectTerms)

  const [temsInit, setTermsInit] = useState(false)
  const dispatch = useAppDispatch()
  const blocker = useBlocker(termsChanged || locationsChanged || false)

  const { data, isError } = useGetProjectInfoQuery(
    { id: Number(id) },
    {
      selectFromResult: (result) => {
        const item = result.data?.data?.items?.[0]

        return {
          ...result,
          data: item,
          isError: result.data && 'errors' in result.data,
        }
      },
      skip: !id,
    },
  )

  const [updateLocations, { isLoading: updateLocationsLoading }] =
    usePostProjectUpdateLocationsMutation()
  const [updateTerms, { isLoading: updateTermsLoading }] = usePostProjectUpdateTermsMutation()

  const {
    data: locations,
    isSuccess: locationsIsSuccess,
    isFetching: locationsIsFetching,
  } = useGetProjectLocationsQuery({ projectId: Number(id) }, { skip: !id })

  const {
    data: terms,
    isSuccess: termsIsSuccess,
    isFetching: termsIsFetching,
  } = useGetProjectTermsQuery({ projectId: Number(id) }, { skip: !id })

  const saveLocations = async (checkTerms = false) => {
    updateLocations({ body: { project_id: Number(id), data: locationsList } }).then((response) => {
      if ('error' in response || !response.data.success) {
        return
      } else {
        dispatch(setProjectLocations(response.data.data?.items ?? []))
        setTermsInit(checkTerms)
        dispatch(setTariffUpdated())

        if (!checkTerms) {
          setTimeout(() => navigate(`/projects/${id}/keywords`))
        }
      }
    })
  }

  const saveTerms = () => {
    const data = termsList?.map((term: { terms: any[] }) => ({
      ...term,
      terms: term.terms.filter((item: string) => item !== ''),
    }))

    updateTerms({ body: { project_id: Number(id), data } }).then(() => {
      dispatch(setTariffUpdated())
    })
  }

  useEffect(() => {
    if (locationsIsSuccess && locations?.data?.items) {
      dispatch(setProjectLocations(locations?.data.items))
    }
  }, [locationsIsFetching, navigate])

  useEffect(() => {
    if (termsIsSuccess && terms?.data?.items) {
      dispatch(setProjectTerms(terms?.data.items))
    }
  }, [termsIsFetching, navigate])

  useEffect(() => {
    if (termsIsFetching) return

    if (
      temsInit &&
      tab === 'locations' &&
      terms?.data?.items?.some((item: ITerm) => !item.terms?.length)
    ) {
      dispatch(
        setModal({
          children: <MissingKeywordsModal add={() => navigate(`/projects/${id}/keywords`)} />,
          size: 'md',
        }),
      )
    }

    setTermsInit(true)
  }, [termsIsFetching])

  useEffect(() => {
    if (termsChanged || locationsChanged) {
      setTimeout(() =>
        dispatch(
          setModal({
            children: (
              <UnsavedChangesModal
                onLeave={() => {
                  if (tab === 'keywords') dispatch(setProjectTerms(terms?.data?.items ?? []))
                  if (tab === 'locations')
                    dispatch(setProjectLocations(locations?.data?.items ?? []))

                  setTimeout(() => navigate(blocker.location || '', { replace: true }))
                }}
                onSave={() => {
                  if (tab === 'keywords') saveTerms()
                  if (tab === 'locations') saveLocations(true)
                }}
              />
            ),
            size: 'md',
          }),
        ),
      )
    }
  }, [blocker])

  useEffect(() => {
    if (isError) {
      navigate('/404')
    }
  }, [isError])

  return (
    <CommonMainPage title={t('navigation.project')}>
      <div className={clsx(styles.header)}>
        <div className={clsx(styles.headerLeft)}>
          <IconButton size='lg' onClick={() => navigate(-1)}>
            <IconArrowBack color='#00326D' />
          </IconButton>
          <h2 className={'truncate'}>{data?.name}</h2>
          <IconButton
            className={data?.name ? clsx(styles.visible) : clsx(styles.hidden)}
            size={'lg'}
            onClick={() =>
              dispatch(setModal({ children: <ProjectEditName id={id} name={data?.name ?? ''} /> }))
            }
          >
            <IconPencil color='#00326D' />
          </IconButton>
        </div>
        <TariffButton />
      </div>
      <BpTabs
        active={tab}
        className={clsx(styles.tabs)}
        size={'md'}
        variant={'row'}
        onChange={(activeTab) => {
          navigate(`/projects/${id}/${activeTab}`, { replace: true })
        }}
      >
        <BpTab activeKey={'general'} title={t('pages.project.general')}>
          <ProjectMainSettings />
        </BpTab>
        <BpTab activeKey={'locations'} title={t('pages.project.locations')}>
          <ProjectLocations
            data={locations}
            loading={updateLocationsLoading}
            submit={saveLocations}
          />
        </BpTab>
        <BpTab activeKey={'keywords'} title={t('pages.project.searchTerms')}>
          <ProjectTerms data={terms} loading={updateTermsLoading} />
        </BpTab>
        <BpTab activeKey={'affiliate-tracking'} title={t('pages.project.affiliateTracking')}>
          <ProjectAffiliateRule />
        </BpTab>
        <BpTab
          activeKey={'trademarks'}
          hide={!user.tm_infringements_enabled}
          title={t('pages.project.trademarks')}
        >
          <ProjectTrademarks />
        </BpTab>
      </BpTabs>
    </CommonMainPage>
  )
}
